import { Network } from '@mogul/api-client';
import { defineChain, zeroAddress } from 'viem';
import { bsc, mainnet } from 'wagmi/chains';

import { getWeb3Config } from './utils';

const holesky = defineChain({
  id: 17000,
  network: 'holesky',
  name: 'Holesky',
  nativeCurrency: { name: 'Holesky Ether', symbol: 'ETH', decimals: 18 },
  blockExplorers: {
    etherscan: { name: 'Etherscan', url: 'https://holesky.etherscan.io' },
    default: { name: 'Etherscan', url: 'https://holesky.etherscan.io' },
  },
  rpcUrls: {
    default: { http: ['https://ethereum-holesky.publicnode.com'] },
    public: { http: ['https://ethereum-holesky.publicnode.com'] },
  },
  contracts: {
    multicall3: {
      address: '0xca11bde05977b3631167028862be2a173976ca11',
      blockCreated: 77,
    },
  },
  testnet: true,
});

const bscTestnet = defineChain({
  id: 97,
  name: 'Binance Smart Chain Testnet',
  network: 'bsc-testnet',
  nativeCurrency: {
    decimals: 18,
    name: 'BNB',
    symbol: 'tBNB',
  },
  rpcUrls: {
    default: { http: ['https://data-seed-prebsc-1-s1.binance.org:8545'] },
    public: { http: ['https://data-seed-prebsc-1-s1.binance.org:8545'] },
  },
  blockExplorers: {
    etherscan: { name: 'BscScan', url: 'https://testnet.bscscan.com' },
    default: { name: 'BscScan', url: 'https://testnet.bscscan.com' },
  },
  contracts: {
    multicall3: {
      address: '0xca11bde05977b3631167028862be2a173976ca11',
      blockCreated: 17422483,
    },
  },
  testnet: true,
});

export const testnetChains = {
  eth: holesky,
  bsc: bscTestnet,
} as const;

export const mainnetChains = {
  eth: mainnet,
  bsc,
} as const;

export const testnetContracts = {
  eth: {
    usdt: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    usdc: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    stars: '0x88fd59e1DD3715A98bb66149DA9c944d9E795c12',
    starsEth: '0x7c6c06d21f46664afd7d12197ce105651d29d1b0',
    starsChef: '0x3E279bA5872435A8722562bE1Dbae8CACf34D6B8',
    starsStaking: '0x9198fc7b0B161b20F1c5d1F9AE9fd4f787eb58F9',
    mogulMarketplaceErc721: '0x321BD6C53b7a41d9Fbe84adEA1FAbc99F850A40f',
    mogulMarketplaceErc1155: '0xc5d5c2b6D5667cb41f278B7677883eB64875e850',
    movieVotingNft: '0x316a7c7E3cc126d1461F933fc4b295B84d37E70d',
    movieVotingChef: '0x4be937b3C22F266dcb4f86C98b2Fa40A399EfAb7',
    movieVoting: '0x59c2C7BF410c9051319C4444c80adc8247419948',
  },
  bsc: {
    usdt: '0x7ef95a0FEE0Dd31b22626fA2e10Ee6A223F8a684',
    usdc: '0xbfE6166F821B2FB48b495E6Bfd19F279604B3AFe',
    stars: '0xa2cC9a802b3355D80A3c0DdcC955B1015edE0393',
    starsEth: '0x94B1B31c8e93a5B871c90C67a7508315a8A2ba60',
    /** @todo */
    starsChef: zeroAddress,
    starsStaking: '0x4Fb281eE05d4A17F160a6EE52dA9231036aF31d3',
    mogulMarketplaceErc721: '0xD810f5C206d1A2003365a6ccC6901BF5F8432A36',
    mogulMarketplaceErc1155: '0x5eC219b744E076d36926728a27E0dE41ed164e47',
    movieVotingNft: '0x9688c438d20df4658ced493ced0cb5b543e1794a',
    movieVotingChef: '0xD0eA5eD2181beB5E680fBB8352236f154F44111b',
    movieVoting: '0xD55838CCD4460E0329Ac7eD25E3F59e19FDC9b08',
    marketplace: '0x5eC219b744E076d36926728a27E0dE41ed164e47',
  },
} as const;

export const mainnetContracts = {
  eth: {
    usdt: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    usdc: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    stars: '0x88fd59e1DD3715A98bb66149DA9c944d9E795c12',
    starsEth: '0x7c6c06d21f46664afd7d12197ce105651d29d1b0',
    starsChef: '0x3E279bA5872435A8722562bE1Dbae8CACf34D6B8',
    starsStaking: '0x88fd59e1DD3715A98bb66149DA9c944d9E795c12',
    mogulMarketplaceErc721: '0x761317D378FB401829ecA5513189F7F91F122444',
    mogulMarketplaceErc1155: '0x07BFB924F29Fc02f4A92E608bBFf4d63ee9ceF89',
    movieVotingNft: '0x316a7c7E3cc126d1461F933fc4b295B84d37E70d',
    movieVotingChef: '0x4be937b3C22F266dcb4f86C98b2Fa40A399EfAb7',
    movieVoting: '0x59c2C7BF410c9051319C4444c80adc8247419948',
  },
  bsc: {
    usdt: '0x55d398326f99059fF775485246999027B3197955',
    usdc: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
    stars: '0x88fd59e1DD3715A98bb66149DA9c944d9E795c12',
    starsEth: '0xEDB1F18aE8AeE807a7A6fA1B03Cc02326AA3a95D',
    /** @todo */
    starsChef: zeroAddress,
    starsStaking: '0xCf23Cc9fCEB15e7e8948162A06b14B5e07B8a1A8',
    mogulMarketplaceErc721: '0x761317D378FB401829ecA5513189F7F91F122444',
    mogulMarketplaceErc1155: '0x07BFB924F29Fc02f4A92E608bBFf4d63ee9ceF89',
    movieVotingNft: '0x5924b03398267ce4836530ab5c15c4da459c0506',
    movieVotingChef: '0x7aed2B343e6E97577717553FCb70F54f2aaf73ff',
    movieVoting: '0xf948250C81563084B8f0d00724223eefabaA035c',
  },
} as const;

export const WEB3_CONFIG = getWeb3Config();

export const CURRENT_CHAIN_IDS_MAP = {
  [WEB3_CONFIG.chains.eth.id]: WEB3_CONFIG.chains.eth,
  [WEB3_CONFIG.chains.bsc.id]: WEB3_CONFIG.chains.bsc,
};

export const CHAIN_ID_TO_TYPE_MAP = {
  [mainnetChains.eth.id]: Network.Mainnet,
  [mainnetChains.bsc.id]: Network.Binance,
  // [testnetChains.eth.id]: 'HOLESKY',
  [testnetChains.eth.id]: Network.Mainnet,
  [testnetChains.bsc.id]: Network.Bsctest,
} as const;

export const CHAIN_ID_TO_KEY_MAP = {
  [mainnetChains.eth.id]: 'eth',
  [mainnetChains.bsc.id]: 'bsc',
  [testnetChains.eth.id]: 'eth',
  [testnetChains.bsc.id]: 'bsc',
} as const;

export const CHAIN_ID_TO_MORALIS = {
  [testnetChains.bsc.id]: 'bsc testnet',
  [mainnetChains.bsc.id]: 'bsc',
} as const;
